import { ApolloLink, Observable } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { refreshCsrfToken } from "common/utils/csrf-token-manager";
import { authToken } from "./authToken";

/**
 * Sets the X-CSRF-Token header dynamically on every request.
 */
export const csrfHeaderLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
    headers: {
      ...headers,
      "X-CSRF-Token": authToken()
    }
  }));
  return forward(operation);
});

/**
 * Intercepts 401 responses, refreshes the CSRF token, and retries once.
 *
 * Apollo v4's ErrorLink receives a unified `error` object (not `networkError`).
 * For HTTP errors, the raw error thrown by the downstream link is passed here
 * directly. Rails' CSRF check returns a 401, so we check `error.statusCode`.
 * The `__csrfRetryAttempted` flag in the operation context prevents infinite
 * retry loops if the refresh itself or the retried request returns another 401.
 */
const CSRF_RETRY_KEY = "__csrfRetryAttempted";

export const csrfRetryLink: ErrorLink = new ErrorLink(({ error, operation, forward }) => {
  const statusCode = (error as any)?.statusCode;

  if (statusCode !== 401) {
    return undefined;
  }

  // Only retry once per operation to prevent infinite 401 loops
  const context = operation.getContext();
  if (context[CSRF_RETRY_KEY]) return undefined;

  operation.setContext({ [CSRF_RETRY_KEY]: true });

  return new Observable(observer => {
    let cancelled = false;
    let innerSub: { unsubscribe(): void } | undefined;

    refreshCsrfToken("design-lab-graphql")
      .then(success => {
        if (cancelled) return;

        if (!success) {
          observer.error(error);
          return;
        }

        innerSub = forward(operation).subscribe(observer);
      })
      .catch(() => {
        if (!cancelled) observer.error(error);
      });

    return () => {
      cancelled = true;
      innerSub?.unsubscribe();
    };
  });
});
